import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AssistantDashboard from "../assistant-dashboard/AssistantDashboard";
import AssistantsList from "../assistants/AssistantsList";
import DashboardLayout from "../dashboard/layout/DashboardLayout";
import { Assistant } from "../assistants/AssistantsList";

export default function AssistantsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const pathAssistantId = (params["*"] ?? "").split("/")[0] || undefined;
  const [filteredAssistants, setFilteredAssistants] = useState<Assistant[]>([]);
  const [showTypeSelectionDialog, setShowTypeSelectionDialog] = useState(false);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    // Check for path parameter first
    if (pathAssistantId) {
      setSelectedAssistantId(pathAssistantId);
    } else {
      // Fall back to query parameter for backwards compatibility
      const searchParams = new URLSearchParams(location.search);
      const assistantId = searchParams.get("id");
      if (assistantId) {
        setSelectedAssistantId(assistantId);
        searchParams.delete("id");
        const qs = searchParams.toString();
        navigate(`/assistants/${assistantId}${qs ? `?${qs}` : ""}`, { replace: true });
      }
    }
  }, [location, pathAssistantId, navigate]);

  const handleAssistantSelect = (id: string) => {
    setSelectedAssistantId(id);
    const search = location.search ? location.search : "";
    navigate(`/assistants/${id}${search}`, { replace: true });
  };
  
  const handleAssistantDeleted = (id: string) => {
    setFilteredAssistants(filteredAssistants.filter((assistant) => assistant.id !== id));
    setSelectedAssistantId(null);
  };

  const handleAssistantUpdated = (id: string, updates: { name?: string; status?: string }) => {
    setFilteredAssistants((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a))
    );
  };

  const cancelAssistantSelection = (query?: string) => {
    setSelectedAssistantId(null);
    window.history.replaceState({}, "", `/assistants${query ? `${query}` : ""}`);
  };
  
  return (
    <DashboardLayout activeItem="Assistants" hideHeader>
      <div className="-ml-4 sm:-ml-6 flex flex-1 min-h-0 overflow-hidden h-[calc(100svh-3rem)]">
        <div className="w-[21.6rem] border-r border-border flex flex-col min-h-0 overflow-hidden shrink-0">
          <AssistantsList
            onAssistantSelect={handleAssistantSelect}
            selectedAssistantId={selectedAssistantId}
            className={null}
            filteredAssistants={filteredAssistants}
            setFilteredAssistants={setFilteredAssistants}
            showTypeSelectionDialog={showTypeSelectionDialog}
            setShowTypeSelectionDialog={setShowTypeSelectionDialog}
            onAssistantRenamed={(id, name) => handleAssistantUpdated(id, { name })}
          />
        </div>
        <div className="flex-1 p-4 overflow-auto min-h-0">
          {selectedAssistantId ? (
            <AssistantDashboard assistantId={selectedAssistantId} onAssistantDeleted={handleAssistantDeleted} onCancelAssistantSelection={cancelAssistantSelection} onAssistantUpdated={handleAssistantUpdated} listAssistantName={filteredAssistants.find((a) => a.id === selectedAssistantId)?.name} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">
                Select an assistant to edit or create a new one
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
